import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { ConversationListResponse } from '@sdr-agend/admin-schemas'

/**
 * Polls the count of conversations sitting in `human_handoff` — 2026-07-16
 * incident follow-up: when `system_settings.handoff_operators` is empty (or
 * an operator misses the WhatsApp alert), a customer escalated to a human can
 * sit unanswered indefinitely with no other signal in the admin. This powers
 * a persistent top-level banner (`HandoffAlertBanner`) instead of relying on
 * an operator to notice the status badge while browsing `/conversations`.
 *
 * Reuses the existing `GET /admin/conversations?status=...` list endpoint's
 * `total` field — no new backend endpoint needed (the list query already
 * supports filtering by status/agentSilenced and returns the total count).
 *
 * Filters on `agentSilenced=true` in ADDITION to `status=human_handoff` —
 * `status` intentionally stays `human_handoff` after a release (Story 6.1
 * Decision #4), so filtering on status alone would keep counting resolved
 * conversations forever. `agentSilenced` is the field that actually reflects
 * whether the AI is currently silenced (2026-07-16 finding).
 */
export function useHandoffAlertCount(): { count: number; isLoading: boolean } {
  const query = useQuery<ConversationListResponse>({
    queryKey: ['admin-handoff-alert-count'],
    queryFn: async () =>
      api<ConversationListResponse>(
        '/admin/conversations?status=human_handoff&agentSilenced=true&perPage=1',
      ),
    staleTime: 20_000,
    refetchInterval: 30_000,
  })

  return { count: query.data?.total ?? 0, isLoading: query.isLoading }
}
